import React from "react";
import { Control, useFormContext } from "react-hook-form";
import { Box } from "@mui/material";
import moment from "moment";
import { FormInputDate } from "../forms";

interface DateOfBirthFieldProps {
  name?: string;
  label?: string;
  format?: string;
  control: Control<any>;
  disabled?: boolean;
  disableFuture?: boolean;
  required?: boolean;
  sx?: any;
  getValues?: (name?: string | string[]) => any; // Optional prop for when form context isn't available
}

const DateOfBirthField: React.FC<DateOfBirthFieldProps> = ({
  name = "dob",
  label: baseLabel = "Date of birth",
  format = "DD/MM/YYYY",
  control,
  disabled = false,
  disableFuture = true,
  required = false,
  sx,
  getValues: getValuesProp,
}) => {
  // Try to get getValues from form context, fall back to prop if provided
  // Always call useFormContext (hooks must be called unconditionally)
  // If form context is not available, useFormContext will throw, so we need the prop
  let formContext: ReturnType<typeof useFormContext> | null = null;
  try {
    formContext = useFormContext();
  } catch {
    // Form context not available - this is expected when used outside FormProvider
  }

  const getValues: (name?: string | string[]) => any = getValuesProp || formContext?.getValues || (() => null);
  const [label, setLabel] = React.useState<string>(baseLabel);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastValueRef = React.useRef<any>(null);
  const isFocusedRef = React.useRef<boolean>(false);

  // Calculate age in years and months
  const calculateAge = React.useCallback((dob: any): string | null => {
    if (!dob) return null;

    let birthDate: moment.Moment;

    if (moment.isMoment(dob)) {
      birthDate = dob;
    } else if (typeof dob === "string") {
      birthDate = moment(dob, ["YYYY-MM-DD", "DD/MM/YYYY"], true);
    } else if (dob instanceof Date) {
      birthDate = moment(dob);
    } else {
      return null;
    }

    if (!birthDate.isValid()) return null;

    const today = moment();
    const years = today.diff(birthDate, "years");

    // Don't show age if older than 130 years (likely invalid date)
    if (years > 130) return null;

    const months = today.diff(birthDate.clone().add(years, "years"), "months");

    return `${years}Y ${months}M`;
  }, []);

  // Handle focus/blur to prevent label updates during typing
  const handleFocus = React.useCallback(() => {
    isFocusedRef.current = true;
  }, []);

  const handleBlur = React.useCallback(() => {
    isFocusedRef.current = false;
    // Update label when blurring if we have a valid date
    const currentValue = getValues(name);
    if (currentValue) {
      const calculatedAge = calculateAge(currentValue);
      setLabel(calculatedAge ? `${baseLabel} (${calculatedAge})` : baseLabel);
    }
  }, [getValues, calculateAge, baseLabel, name]);

  // Poll for changes without subscribing to form (avoids re-renders during typing)
  React.useEffect(() => {
    const updateLabel = () => {
      // Don't update label while user is typing
      if (isFocusedRef.current) {
        return;
      }

      const currentValue = getValues(name);

      // Only update if value actually changed
      if (currentValue !== lastValueRef.current) {
        lastValueRef.current = currentValue;

        // Clear existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Only update label if we have a valid, complete date (not a partial string)
        const isValidCompleteDate = currentValue && (
          moment.isMoment(currentValue) ||
          (currentValue instanceof Date) ||
          (typeof currentValue === "string" && moment(currentValue, ["YYYY-MM-DD", "DD/MM/YYYY"], true).isValid())
        );

        // Debounce the label update, and only if date is complete
        timeoutRef.current = setTimeout(() => {
          // Don't update if user started typing
          if (isFocusedRef.current) {
            return;
          }

          // Double-check the value is still valid before updating
          const valueToCheck = getValues(name);
          if (isValidCompleteDate && valueToCheck === currentValue) {
            const calculatedAge = calculateAge(valueToCheck);
            const newLabel = calculatedAge ? `${baseLabel} (${calculatedAge})` : baseLabel;
            setLabel(newLabel);
          } else if (!valueToCheck) {
            // Clear label if value was cleared
            setLabel(baseLabel);
          }
        }, 2000); // Long debounce to avoid updates during active typing
      }
    };

    // Initial calculation
    const dobValue = getValues(name);
    lastValueRef.current = dobValue;
    const calculatedAge = calculateAge(dobValue);
    setLabel(calculatedAge ? `${baseLabel} (${calculatedAge})` : baseLabel);

    // Check every 1000ms (less frequent to reduce overhead)
    const intervalId = setInterval(updateLabel, 1000);

    return () => {
      clearInterval(intervalId);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [getValues, calculateAge, baseLabel, name]);

  return (
    <Box sx={{ flex: 1, ...sx }}>
      <FormInputDate
        disableFuture={disableFuture}
        minDate={moment().subtract(120, "years")}
        name={name}
        label={label}
        format={format}
        control={control}
        disabled={disabled}
        required={required}
        slotProps={{
          textField: {
            onFocus: handleFocus,
            onBlur: handleBlur,
            autoComplete: "off",
            slotProps: {
              htmlInput: {
                autoComplete: "off",
              },
            },
          }
        }}
      />
    </Box>
  );
};

export default DateOfBirthField;
